import React from "react";
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Btn, Card, Field, Input, Select, Textarea } from "../ui";
import { ImageField, ListField } from "./websiteFields";
import { DEFAULT_LOGO_HEIGHT } from "../../components/BrandRingLogo";

const YN = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }];
const yn = (v) => (v === false ? "no" : "yes");
const rid = (p) => `${p}_${Math.random().toString(36).slice(2, 7)}`;

const Toggle = ({ label, value, onChange, testId, hint }) => (
    <Field label={label} hint={hint}>
        <Select value={yn(value)} options={YN} data-testid={testId} onChange={(e) => onChange(e.target.value === "yes")} />
    </Field>
);

const newNode = (p) => ({ id: rid(p), label: "New link", href: "/", visible: true, children: [] });

const setAt = (list, path, updater) => {
    const [i, ...rest] = path;
    return list.map((item, j) => {
        if (j !== i) return item;
        if (!rest.length) return updater(item);
        return { ...item, children: setAt(item.children || [], rest, updater) };
    });
};

const listAt = (list, path, updater) => {
    if (path.length === 1) return updater(list);
    const [i, ...rest] = path;
    return list.map((item, j) => (j === i
        ? { ...item, children: listAt(item.children || [], rest, updater) } : item));
};

const removeAt = (list, path) => {
    const at = path[path.length - 1];
    return listAt(list, path, (l) => l.filter((_, k) => k !== at));
};

const moveAt = (list, path, delta) => {
    const at = path[path.length - 1];
    return listAt(list, path, (l) => {
        const to = at + delta;
        if (to < 0 || to >= l.length) return l;
        const copy = [...l];
        [copy[at], copy[to]] = [copy[to], copy[at]];
        return copy;
    });
};

// One editable row, used at all three menu levels.
const MenuRow = ({ node, path, count, prefix, menu, setMenu }) => {
    const at = path[path.length - 1];
    return (
        <div className="grid md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
            <Field label="Label"><Input value={node.label || ""} data-testid={`${prefix}-label-${node.id}`}
                onChange={(e) => setMenu(setAt(menu, path, (x) => ({ ...x, label: e.target.value })))} /></Field>
            <Field label="Link"><Input value={node.href || ""} data-testid={`${prefix}-href-${node.id}`}
                onChange={(e) => setMenu(setAt(menu, path, (x) => ({ ...x, href: e.target.value })))} /></Field>
            <Toggle label="Visible" value={node.visible} testId={`${prefix}-visible-${node.id}`}
                onChange={(v) => setMenu(setAt(menu, path, (x) => ({ ...x, visible: v })))} />
            <div className="flex gap-1 pb-2">
                <button disabled={at === 0} onClick={() => setMenu(moveAt(menu, path, -1))}
                    data-testid={`${prefix}-up-${node.id}`} className="text-zinc-500 hover:text-white disabled:opacity-30">
                    <ArrowUp className="w-4 h-4" />
                </button>
                <button disabled={at === count - 1} onClick={() => setMenu(moveAt(menu, path, 1))}
                    data-testid={`${prefix}-down-${node.id}`} className="text-zinc-500 hover:text-white disabled:opacity-30">
                    <ArrowDown className="w-4 h-4" />
                </button>
                <button onClick={() => setMenu(removeAt(menu, path))}
                    data-testid={`${prefix}-delete-${node.id}`} className="text-zinc-500 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export const HeaderEditor = ({ globals, onChange }) => {
    const h = globals.header || {};
    const a = globals.announcement || {};
    const setH = (patch) => onChange({ ...globals, header: { ...h, ...patch } });
    const setA = (patch) => onChange({ ...globals, announcement: { ...a, ...patch } });
    const menu = h.menu || [];
    const setMenu = (list) => setH({ menu: list });

    return (
        <div className="space-y-4" data-testid="header-editor">
            <Card title="Announcement bar">
                <div className="grid md:grid-cols-4 gap-4">
                    <Toggle label="Show bar" value={a.enabled} onChange={(v) => setA({ enabled: v })} testId="announce-enabled" />
                    <Field label="Message" className="md:col-span-3">
                        <Input value={a.text || ""} data-testid="announce-text" onChange={(e) => setA({ text: e.target.value })} />
                    </Field>
                    <Field label="Link label"><Input value={a.link_label || ""} data-testid="announce-link-label"
                        onChange={(e) => setA({ link_label: e.target.value })} /></Field>
                    <Field label="Link address"><Input value={a.link_href || ""} data-testid="announce-link-href"
                        onChange={(e) => setA({ link_href: e.target.value })} /></Field>
                    <Field label="Colour"><Select value={a.tone || "gold"} options={["gold", "lime"]} data-testid="announce-tone"
                        onChange={(e) => setA({ tone: e.target.value })} /></Field>
                </div>
            </Card>

            <Card title="Logo & header buttons" action={
                <Btn variant="ghost" data-testid="logo-reset"
                    onClick={() => setH({ logo_url: "", logo_height: DEFAULT_LOGO_HEIGHT })}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1 inline" /> RESET LOGO
                </Btn>}>
                <div className="grid md:grid-cols-3 gap-4">
                    <ImageField field={{ key: "logo_url", label: "Logo image" }} value={h.logo_url}
                        onChange={(v) => setH({ logo_url: v })} />
                    <Field label="Logo height (px)"
                        hint={`Leave the logo image empty to use the animated ring mark. Default ${DEFAULT_LOGO_HEIGHT}px; mobile scales to 82%.`}>
                        <Input type="number" value={h.logo_height ?? DEFAULT_LOGO_HEIGHT} data-testid="logo-height"
                            onChange={(e) => setH({ logo_height: Number(e.target.value) })} /></Field>
                    <Toggle label="Sticky header" value={h.sticky} onChange={(v) => setH({ sticky: v })} testId="header-sticky" />
                    <Toggle label="Account icon" value={h.show_account} onChange={(v) => setH({ show_account: v })} testId="header-account" />
                    <Toggle label="Light/dark switch icon" value={h.show_theme} onChange={(v) => setH({ show_theme: v })}
                        testId="header-theme" hint="Only appears if the theme option is ON in CRM → Settings" />
                    <Toggle label="Cart button" value={h.show_cart} onChange={(v) => setH({ show_cart: v })} testId="header-cart" />
                    <Toggle label="Consult button" value={h.show_consult} onChange={(v) => setH({ show_consult: v })} testId="header-consult" />
                    <Field label="Consult label"><Input value={h.consult_label || ""} data-testid="header-consult-label"
                        onChange={(e) => setH({ consult_label: e.target.value })} /></Field>
                    <Field label="Consult link"><Input value={h.consult_href || ""} data-testid="header-consult-href"
                        onChange={(e) => setH({ consult_href: e.target.value })} /></Field>
                    <Toggle label="Main button" value={h.show_cta} onChange={(v) => setH({ show_cta: v })} testId="header-cta" />
                    <Field label="Button label"><Input value={h.cta_label || ""} data-testid="header-cta-label"
                        onChange={(e) => setH({ cta_label: e.target.value })} /></Field>
                    <Field label="Button link"><Input value={h.cta_href || ""} data-testid="header-cta-href"
                        onChange={(e) => setH({ cta_href: e.target.value })} /></Field>
                </div>
            </Card>

            <Card title="Header spacing">
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Space above the logo row"
                        hint="e.g. 12px, 1rem — blank keeps the default header height">
                        <Input value={h.space_top ?? ""} placeholder="default" data-testid="header-space-top"
                            onChange={(e) => setH({ space_top: e.target.value })} /></Field>
                    <Field label="Space below the logo row" hint="e.g. 12px, 1rem">
                        <Input value={h.space_bottom ?? ""} placeholder="default" data-testid="header-space-bottom"
                            onChange={(e) => setH({ space_bottom: e.target.value })} /></Field>
                </div>
                <p className="mt-3 text-xs text-zinc-500">
                    The gap between the header and the first section belongs to that section — set it in
                    Website → Pages → pick the section → “Space above” (a minus value like -40px pulls it up).
                </p>
            </Card>

            <Card title={`Menu (${menu.length} items)`} action={
                <Btn onClick={() => setMenu([...menu, { id: rid("m"), label: "New item", href: "/", visible: true, children: [] }])}
                    data-testid="menu-add-item"><Plus className="w-3.5 h-3.5 mr-1 inline" /> ITEM</Btn>
            }>
                <p className="text-sm text-zinc-500 mb-4">
                    Up to three levels: menu item → dropdown → sub-dropdown. Switch any level off to hide it
                    from the website without deleting it.
                </p>
                <div className="space-y-3">
                    {menu.map((m, i) => (
                        <div key={m.id} className="border border-white/10 p-3" data-testid={`menu-item-${m.id}`}>
                            <MenuRow node={m} path={[i]} count={menu.length} prefix="menu" menu={menu} setMenu={setMenu} />

                            <div className="mt-3 pl-4 border-l border-white/10 space-y-3">
                                <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">DROPDOWN ITEMS</div>
                                {(m.children || []).map((c, ci) => (
                                    <div key={c.id} className="border border-white/8 p-3" data-testid={`submenu-${c.id}`}>
                                        <MenuRow node={c} path={[i, ci]} count={(m.children || []).length}
                                            prefix="submenu" menu={menu} setMenu={setMenu} />

                                        <div className="mt-3 pl-4 border-l border-white/8 space-y-2">
                                            <div className="mono text-[9px] tracking-[0.16em] text-zinc-600">SUB-DROPDOWN</div>
                                            {(c.children || []).map((g, gi) => (
                                                <div key={g.id} data-testid={`subsubmenu-${g.id}`}>
                                                    <MenuRow node={g} path={[i, ci, gi]} count={(c.children || []).length}
                                                        prefix="subsubmenu" menu={menu} setMenu={setMenu} />
                                                </div>
                                            ))}
                                            <Btn variant="ghost" data-testid={`subsubmenu-add-${c.id}`}
                                                onClick={() => setMenu(setAt(menu, [i, ci], (x) => ({
                                                    ...x, children: [...(x.children || []), newNode("g")] })))}>
                                                <Plus className="w-3 h-3 mr-1 inline" /> SUB LINK
                                            </Btn>
                                        </div>
                                    </div>
                                ))}
                                <Btn variant="ghost" data-testid={`submenu-add-${m.id}`}
                                    onClick={() => setMenu(setAt(menu, [i], (x) => ({
                                        ...x, children: [...(x.children || []), newNode("s")] })))}>
                                    <Plus className="w-3 h-3 mr-1 inline" /> DROPDOWN LINK
                                </Btn>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
};

export const FooterEditor = ({ globals, onChange }) => {
    const f = globals.footer || {};
    const setF = (patch) => onChange({ ...globals, footer: { ...f, ...patch } });
    const columns = f.columns || [];
    const setColumns = (list) => setF({ columns: list });
    const blocks = f.blocks || [];
    const setBlocks = (list) => setF({ blocks: list });
    const socials = f.socials || [];

    return (
        <div className="space-y-4" data-testid="footer-editor">
            <Card title="Footer basics">
                <div className="grid md:grid-cols-3 gap-4">
                    <Toggle label="Show footer" value={f.visible} onChange={(v) => setF({ visible: v })} testId="footer-visible" />
                    <ImageField field={{ key: "footer_logo", label: "Logo image" }} value={f.logo_url} onChange={(v) => setF({ logo_url: v })} />
                    <Field label="Tagline"><Input value={f.tagline || ""} data-testid="footer-tagline"
                        onChange={(e) => setF({ tagline: e.target.value })} /></Field>
                    <Field label="Legal name" hint="Shown above the about text, e.g. Rehealthme Healthcare">
                        <Input value={f.legal_name || ""} data-testid="footer-legal-name-input"
                            onChange={(e) => setF({ legal_name: e.target.value })} /></Field>
                    <Field label="Space above the footer" hint="e.g. 80px, 6% — a minus value pulls it closer">
                        <Input value={f.space_top ?? ""} placeholder="default" data-testid="footer-space-top"
                            onChange={(e) => setF({ space_top: e.target.value })} /></Field>
                    <Field label="Space below the footer" hint="e.g. 40px">
                        <Input value={f.space_bottom ?? ""} placeholder="default" data-testid="footer-space-bottom"
                            onChange={(e) => setF({ space_bottom: e.target.value })} /></Field>
                    <Field label="About text" className="md:col-span-3"><Textarea rows={3} value={f.about || ""} data-testid="footer-about"
                        onChange={(e) => setF({ about: e.target.value })} /></Field>
                    <Toggle label="Newsletter form" value={f.newsletter_enabled} onChange={(v) => setF({ newsletter_enabled: v })} testId="footer-newsletter" />
                    <Field label="Newsletter placeholder"><Input value={f.newsletter_placeholder || ""} data-testid="footer-newsletter-placeholder"
                        onChange={(e) => setF({ newsletter_placeholder: e.target.value })} /></Field>
                    <Field label="Big wordmark" hint="Leave empty to hide"><Input value={f.wordmark ?? ""} data-testid="footer-wordmark"
                        onChange={(e) => setF({ wordmark: e.target.value })} /></Field>
                </div>
            </Card>

            <Card title={`Link columns (${columns.length})`} action={
                <Btn data-testid="footer-add-column"
                    onClick={() => setColumns([...columns, { id: rid("f"), title: "New column", visible: true, links: [] }])}>
                    <Plus className="w-3.5 h-3.5 mr-1 inline" /> COLUMN</Btn>
            }>
                <div className="grid md:grid-cols-2 gap-3">
                    {columns.map((col, i) => (
                        <div key={col.id} className="border border-white/10 p-3 space-y-2" data-testid={`footer-col-${col.id}`}>
                            <div className="flex items-end gap-2">
                                <Field label="Column title" className="flex-1"><Input value={col.title || ""} data-testid={`footer-col-title-${col.id}`}
                                    onChange={(e) => setColumns(columns.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} /></Field>
                                <Toggle label="Visible" value={col.visible} testId={`footer-col-visible-${col.id}`}
                                    onChange={(v) => setColumns(columns.map((x, j) => (j === i ? { ...x, visible: v } : x)))} />
                                <button onClick={() => setColumns(columns.filter((_, j) => j !== i))}
                                    data-testid={`footer-col-delete-${col.id}`} className="text-zinc-500 hover:text-red-400 pb-2">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            {(col.links || []).map((l, li) => (
                                <div key={l.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                                    <Input value={l.label || ""} data-testid={`footer-link-label-${l.id}`}
                                        onChange={(e) => setColumns(columns.map((x, j) => (j === i
                                            ? { ...x, links: x.links.map((y, k) => (k === li ? { ...y, label: e.target.value } : y)) } : x)))} />
                                    <Input value={l.href || ""} data-testid={`footer-link-href-${l.id}`}
                                        onChange={(e) => setColumns(columns.map((x, j) => (j === i
                                            ? { ...x, links: x.links.map((y, k) => (k === li ? { ...y, href: e.target.value } : y)) } : x)))} />
                                    <button onClick={() => setColumns(columns.map((x, j) => (j === i
                                        ? { ...x, links: x.links.filter((_, k) => k !== li) } : x)))}
                                        data-testid={`footer-link-delete-${l.id}`} className="text-zinc-500 hover:text-red-400">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            <Btn variant="ghost" data-testid={`footer-col-add-link-${col.id}`}
                                onClick={() => setColumns(columns.map((x, j) => (j === i
                                    ? { ...x, links: [...(x.links || []), { id: rid("fl"), label: "New link", href: "/" }] } : x)))}>
                                <Plus className="w-3 h-3 mr-1 inline" /> LINK
                            </Btn>
                        </div>
                    ))}
                </div>
            </Card>

            <Card title={`Extra footer sections (${blocks.length})`} action={
                <div className="flex gap-2">
                    <Btn variant="ghost" data-testid="footer-add-text-block"
                        onClick={() => setBlocks([...blocks, {
                            id: rid("fb"), type: "text", title: "New section",
                            text: "Write anything here — an address, a disclaimer, opening hours.",
                            image_url: "", image_alt: "", href: "", link_label: "", width: "third", visible: true,
                        }])}>
                        <Plus className="w-3.5 h-3.5 mr-1 inline" /> TEXT</Btn>
                    <Btn variant="ghost" data-testid="footer-add-image-block"
                        onClick={() => setBlocks([...blocks, {
                            id: rid("fb"), type: "image", title: "", text: "",
                            image_url: "", image_alt: "", href: "", link_label: "", width: "third", visible: true,
                        }])}>
                        <Plus className="w-3.5 h-3.5 mr-1 inline" /> IMAGE</Btn>
                </div>
            }>
                <div className="grid md:grid-cols-2 gap-3">
                    {blocks.map((b, i) => {
                        const setB = (patch) => setBlocks(blocks.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                        return (
                            <div key={b.id} className="border border-white/10 p-3 space-y-2" data-testid={`footer-block-row-${b.id}`}>
                                <div className="flex items-center justify-between">
                                    <span className="mono text-[9px] tracking-[0.16em] text-zinc-500">
                                        SECTION {i + 1} · {(b.type || "text").toUpperCase()}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => {
                                            if (i === 0) return;
                                            const list = [...blocks];
                                            [list[i - 1], list[i]] = [list[i], list[i - 1]];
                                            setBlocks(list);
                                        }} data-testid={`footer-block-up-${b.id}`} className="text-zinc-500 hover:text-white">
                                            <ArrowUp className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => setBlocks(blocks.filter((_, j) => j !== i))}
                                            data-testid={`footer-block-delete-${b.id}`} className="text-zinc-500 hover:text-red-400">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <Field label="Heading (optional)">
                                    <Input value={b.title || ""} data-testid={`footer-block-title-${b.id}`}
                                        onChange={(e) => setB({ title: e.target.value })} /></Field>
                                <Field label="Text">
                                    <Textarea rows={3} value={b.text || ""} data-testid={`footer-block-text-${b.id}`}
                                        onChange={(e) => setB({ text: e.target.value })} /></Field>
                                <ImageField field={{ key: `footer-block-image-${b.id}`, label: "Image (optional)" }}
                                    value={b.image_url} onChange={(v) => setB({ image_url: v })} />
                                <div className="grid grid-cols-2 gap-2">
                                    <Field label="Image alt text">
                                        <Input value={b.image_alt || ""} data-testid={`footer-block-alt-${b.id}`}
                                            onChange={(e) => setB({ image_alt: e.target.value })} /></Field>
                                    <Field label="Width">
                                        <Select value={b.width || "third"} data-testid={`footer-block-width-${b.id}`}
                                            onChange={(e) => setB({ width: e.target.value })}
                                            options={[{ value: "third", label: "One third" },
                                                { value: "half", label: "Half" },
                                                { value: "two_thirds", label: "Two thirds" },
                                                { value: "full", label: "Full width" }]} /></Field>
                                    <Field label="Link (optional)">
                                        <Input value={b.href || ""} data-testid={`footer-block-href-${b.id}`}
                                            placeholder="/science or https://…"
                                            onChange={(e) => setB({ href: e.target.value })} /></Field>
                                    <Field label="Link label">
                                        <Input value={b.link_label || ""} data-testid={`footer-block-link-label-${b.id}`}
                                            onChange={(e) => setB({ link_label: e.target.value })} /></Field>
                                </div>
                                <Toggle label="Visible" value={b.visible !== false} testId={`footer-block-visible-${b.id}`}
                                    onChange={(v) => setB({ visible: v })} />
                            </div>
                        );
                    })}
                </div>
            </Card>

            <Card title="Contact, social & legal">
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Email"><Input value={f.contact_email || ""} data-testid="footer-email"
                        onChange={(e) => setF({ contact_email: e.target.value })} /></Field>
                    <Field label="Phone"><Input value={f.contact_phone || ""} data-testid="footer-phone"
                        onChange={(e) => setF({ contact_phone: e.target.value })} /></Field>
                    <Field label="Address"><Input value={f.contact_address || ""} data-testid="footer-address"
                        onChange={(e) => setF({ contact_address: e.target.value })} /></Field>
                    <Field label="Copyright line" className="md:col-span-2" hint="{year} is replaced automatically">
                        <Input value={f.copyright || ""} data-testid="footer-copyright"
                            onChange={(e) => setF({ copyright: e.target.value })} /></Field>
                    <Field label="Side note"><Input value={f.note || ""} data-testid="footer-note"
                        onChange={(e) => setF({ note: e.target.value })} /></Field>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <ListField field={{ key: "badges", label: "Trust badges" }} value={f.badges || []}
                        onChange={(v) => setF({ badges: v })} />
                    <div>
                        <div className="mono text-[10px] tracking-[0.18em] text-zinc-400 mb-2">SOCIAL LINKS</div>
                        <div className="space-y-2">
                            {socials.map((s, i) => (
                                <div key={s.id} className="grid grid-cols-[110px_1fr_1fr_auto] gap-2 items-center" data-testid={`footer-social-row-${s.id}`}>
                                    <Select value={s.platform || "instagram"} data-testid={`footer-social-platform-${s.id}`}
                                        options={["instagram", "facebook", "twitter", "youtube", "linkedin"]}
                                        onChange={(e) => setF({ socials: socials.map((x, j) => (j === i ? { ...x, platform: e.target.value } : x)) })} />
                                    <Input value={s.label || ""} data-testid={`footer-social-label-${s.id}`}
                                        onChange={(e) => setF({ socials: socials.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                                    <Input value={s.href || ""} data-testid={`footer-social-href-${s.id}`}
                                        onChange={(e) => setF({ socials: socials.map((x, j) => (j === i ? { ...x, href: e.target.value } : x)) })} />
                                    <button onClick={() => setF({ socials: socials.filter((_, j) => j !== i) })}
                                        data-testid={`footer-social-delete-${s.id}`} className="text-zinc-500 hover:text-red-400">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            <Btn variant="ghost" data-testid="footer-add-social"
                                onClick={() => setF({ socials: [...socials, { id: rid("s"), platform: "instagram", label: "@rehealthme", href: "https://instagram.com" }] })}>
                                <Plus className="w-3 h-3 mr-1 inline" /> SOCIAL
                            </Btn>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};
